import {
    ActionIcon,
    Box,
    Center,
    Grid,
    Paper,
    Stack,
    Tooltip,
    useMantineTheme
} from '@mantine/core';
import {IoWarning} from 'react-icons/io5';

import {SmallProfileOverflowMenu} from './SmallProfileOverflowMenu';

import {UserAvatar} from './UserAvatar';
import {UserFullName} from './UserFullName';
import {AccountSessionBadge} from '../../account-sessions/components/AccountSessionBadge';
import {useMediaQuery} from '@mantine/hooks';
import {Account} from "../types.ts";
import {getUserNameInfo} from "../utils/userNameUtils.ts";
import {moduleConfig} from "../config/index.ts";

import classes from './SmallProfile.module.css';

export const SmallProfile = ({
                                 account,
                                 onClick,
                                 onSignOut,
                                 onSignOutAndForget,
                                 onHideAccount,
                             }: {
    account: Account,
    onClick: () => void,
    onSignOut: () => void,
    onSignOutAndForget: () => void,
    onHideAccount: () => void,
}) => {
    const theme = useMantineTheme();
    const {isNameComplete} = getUserNameInfo(account);
    const shouldCheckNameCompleteness = moduleConfig.enableNameCompletenessCheck && !isNameComplete;

    const lessThanMedium = useMediaQuery(
        `(max-width: ${theme.breakpoints.md}px)`
    );

    const paperProps = shouldCheckNameCompleteness
        ? { 
            className: classes.container, 
            shadow: 'sm', 
            withBorder: true,
            style: { 
                borderColor: theme.colors.orange[4], 
                backgroundColor: theme.colors.orange[0] 
            }
        }
        : { className: classes.container, shadow: 'sm', withBorder: true };

    return (
        <Paper {...paperProps} style={{...paperProps.style, position: 'relative'}}>
            {shouldCheckNameCompleteness && (
                <Box style={{ position: 'absolute', top: 8, right: 8, zIndex: 10 }}>
                    <Tooltip label="Profile setup incomplete. Please contact your IT department to complete account setup.">
                        <ActionIcon variant="transparent" color="orange" size="sm">
                            <IoWarning size={16} />
                        </ActionIcon>
                    </Tooltip>
                </Box>
            )}
            <Grid gutter="sm" py="sm">
                <Grid.Col
                    span={{
                        base: 3,
                        sm: 12
                    }} 
                    onClick={shouldCheckNameCompleteness ? undefined : onClick}
                    className={shouldCheckNameCompleteness ? undefined : "cursor-pointer"}
                    style={shouldCheckNameCompleteness ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
                >
                    <Center>
                        <UserAvatar user={account} width="50" height="50"/>
                    </Center>
                </Grid.Col>

                <Grid.Col
                    span={{
                        base: 7,
                        sm: 12
                    }}
                    onClick={shouldCheckNameCompleteness ? undefined : onClick}
                    className={shouldCheckNameCompleteness ? undefined : "cursor-pointer"}
                    style={shouldCheckNameCompleteness ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
                >
                    <Stack
                        gap="0px"
                        mt="sm"
                        align={lessThanMedium ? 'center' : 'left'}
                    >
                        <UserFullName user={account}/>
                        {account['emailAddress']}
                    </Stack>
                </Grid.Col>

                <Grid.Col span={{
                    base: 2,
                    sm: 12
                }}>
                    <Center mt="md">
                        <Stack gap="xs" align="center">
                            <AccountSessionBadge account={account} />
                            <SmallProfileOverflowMenu
                                onSignOut={onSignOut}
                                onSignOutAndForget={onSignOutAndForget}
                                onHideAccount={onHideAccount}
                                isNameComplete={!shouldCheckNameCompleteness}
                            />
                        </Stack>
                    </Center>
                </Grid.Col>
            </Grid>
        </Paper>
    );
};
